// Hit-testing using character boxes with space-aware transforms

import { CoordinateSpace, Coordinates, ContentRect } from './CoordinateSpaces';
import { SpaceTransforms } from './SpaceTransforms';
import { BackendLayoutPlan } from './BackendPlanValidator';

export interface HitTestResult {
  characterIndex: number;
  runIndex: number;
  lineIndex: number;
  coordinates: Coordinates;
}

export class HitTestingEngine {
  /**
   * Transform HarfBuzz character boxes to content space for hit testing
   * CharBoxes are in PAGE space, need to convert to CONTENT space
   */
  private static transformHarfBuzzCharacterBoxes(charBoxes: number[][], contentRect: ContentRect): number[][] {
    return charBoxes.map(box => [
      box[0] - contentRect.x,  // x - Convert from PAGE to CONTENT space
      box[1] - contentRect.y,  // y - Convert from PAGE to CONTENT space
      box[2],  // width - Keep original width
      box[3]   // height - Keep original height
    ]);
  }

  /**
   * Perform hit-testing using character boxes with explicit coordinate transforms
   */
  static hitTest(
    canvasX: number,
    canvasY: number,
    plan: BackendLayoutPlan | any,  // Accept any plan structure (raw or transformed)
    contentRect: ContentRect
  ): HitTestResult | null {
    // 🔧 IMPROVEMENT: Convert canvas coordinates to content space first
    // This ensures consistent coordinate handling with character boxes
    const contentCoords: Coordinates = {
      x: canvasX - contentRect.x,  // Subtract content rect offset
      y: canvasY - contentRect.y,  // Subtract content rect offset
      space: CoordinateSpace.CONTENT
    };
    


    // Handle both raw backend structure and transformed frontend structure
    let lines: any[] = [];
    if (plan.lines) {
      // Transformed frontend structure
      lines = plan.lines;
    } else if (plan.pages?.[0]?.frames?.[0]?.lines) {
      // Raw backend structure
      lines = plan.pages[0].frames[0].lines;
    } else {
      console.warn('⚠️ [HIT_TEST] Layout plan has no lines structure:', {
        hasLines: !!plan.lines,
        hasPages: !!plan.pages,
        hasFrames: !!plan.pages?.[0]?.frames,
        hasFrameLines: !!plan.pages?.[0]?.frames?.[0]?.lines
      });
      return null;
    }

    // 🎯 EMPTY DOCUMENT FIX: Handle empty documents with virtual character box
    if (!lines || lines.length === 0 ||
        (lines.length === 1 && (!lines[0].runs || lines[0].runs.length === 0))) {
      
      return {
        characterIndex: 0,
        runIndex: 0,
        lineIndex: 0,
        coordinates: {
          x: contentRect.x,
          y: contentRect.y,
          space: CoordinateSpace.PAGE
        }
      };
    }
    
    // Check for empty runs (charBoxes: [])
    const hasAnyCharBoxes = lines.some(line =>
      line.runs.some((run: any) => run.charBoxes && run.charBoxes.length > 0)
    );
    
    if (!hasAnyCharBoxes) {
      return {
        characterIndex: 0,
        runIndex: 0,
        lineIndex: 0,
        coordinates: {
          x: contentRect.x,
          y: contentRect.y,
          space: CoordinateSpace.PAGE
        }
      };
    }

    let currentCharIndex = 0;
    let closestResult: HitTestResult | null = null;
    let minDistance = Infinity;

    // Search through all lines and runs
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex];

      for (let runIndex = 0; runIndex < line.runs.length; runIndex++) {
        const run = line.runs[runIndex];

        if (!run.text || !run.charBoxes) {
          currentCharIndex += run.text?.length || 0;
          continue;
        }

        // Transform HarfBuzz character boxes to CONTENT coordinate space
        const transformedCharBoxes = this.transformHarfBuzzCharacterBoxes(
          run.charBoxes as number[][],
          contentRect
        );

        // Test each transformed character box
        for (let charIndex = 0; charIndex < transformedCharBoxes.length; charIndex++) {
          const [boxX, boxY, boxWidth, boxHeight] = transformedCharBoxes[charIndex];

          // Character boxes are now in Canvas space, compare with content coordinates
          const contentCharBox = {
            x: boxX,
            y: boxY,
            space: CoordinateSpace.CONTENT
          };


          // Hit test using content space coordinates (with transformed character boxes)
          if (
            contentCoords.x >= boxX &&
            contentCoords.x < boxX + boxWidth &&
            contentCoords.y >= boxY &&
            contentCoords.y < boxY + boxHeight
          ) {
            return {
              characterIndex: currentCharIndex + charIndex,
              runIndex,
              lineIndex,
              coordinates: contentCharBox
            };
          }

          // 🔧 IMPROVEMENT: Track closest character using content space coordinates
          const centerX = boxX + boxWidth / 2;
          const centerY = boxY + boxHeight / 2;
          const distance = Math.sqrt(
            (contentCoords.x - centerX) ** 2 + (contentCoords.y - centerY) ** 2
          );

          if (distance < minDistance) {
            minDistance = distance;
            closestResult = {
              characterIndex: currentCharIndex + charIndex,
              runIndex,
              lineIndex,
              coordinates: contentCharBox
            };
          }
        }

        currentCharIndex += run.text.length;
      }
    }

    return closestResult;
  }

  /**
   * Get character position for caret rendering
   */
  static getCharacterPosition(
    characterIndex: number,
    plan: any,  // Accept any plan structure (raw or transformed)
    contentRect: ContentRect
  ): Coordinates | null {
    let currentCharIndex = 0;

    // Handle both raw backend structure and transformed frontend structure
    let lines: any[] = [];
    if (plan.lines) {
      // Transformed frontend structure
      lines = plan.lines;
    } else if (plan.pages?.[0]?.frames?.[0]?.lines) {
      // Raw backend structure
      lines = plan.pages[0].frames[0].lines;
    } else {
      console.warn('⚠️ [GET_CHARACTER_POSITION] Layout plan has no lines structure');
      return null;
    }

    for (const line of lines) {
      for (const run of line.runs as any[]) {
        if (!run.text || !run.charBoxes) {
          currentCharIndex += run.text?.length || 0;
          continue;
        }

        const runEndIndex = currentCharIndex + run.text.length;

        if (characterIndex <= runEndIndex) {
          const runCharIndex = characterIndex - currentCharIndex;

          if (runCharIndex < run.charBoxes.length) {
            // Transform HarfBuzz character box to CONTENT space
            const transformedCharBox = this.transformHarfBuzzCharacterBoxes(
              [run.charBoxes[runCharIndex] as number[]],
              contentRect
            )[0];
            const [boxX, boxY, boxWidth, boxHeight] = transformedCharBox;

            // Transform from content space to page space using existing system
            const contentCoords: Coordinates = {
              x: boxX,
              y: line.baselineY_pagePx, // Use explicit baseline
              space: CoordinateSpace.CONTENT
            };

            return SpaceTransforms.transform(
              contentCoords,
              CoordinateSpace.PAGE,
              { contentRect }
            );
          }
        }

        currentCharIndex = runEndIndex;
      }
    }

    return null;
  }
}
